import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useThemeStore } from '@/stores/theme-store';
import { useAuthStore } from '@/stores/auth-store';
import { usePortalSettings } from '@/hooks/use-settings';
import { useNavCounts, useMarkSectionRead } from '@/hooks/use-notifications';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { NotificationBell } from '@/components/notifications/notification-bell';
import { toast } from '@/hooks/use-toast';

import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  Clock,
  Users,
  Users2,
  Ticket,
  Receipt,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  Menu,
  X,
  Search,
  CalendarDays,
  Megaphone,
  Activity,
  FileText,
  HeartPulse,
} from 'lucide-react';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  roles?: string[];
}

const navItems: NavItem[] = [
  { label: 'nav.dashboard', href: '/', icon: LayoutDashboard },
  { label: 'nav.projects', href: '/projects', icon: FolderKanban },
  { label: 'nav.tasks', href: '/tasks', icon: CheckSquare },
  { label: 'nav.timeTracking', href: '/time', icon: Clock, roles: ['admin', 'project_manager'] },
  { label: 'nav.users', href: '/users', icon: Users, roles: ['admin'] },
  { label: 'nav.leads', href: '/leads', icon: Megaphone, roles: ['admin', 'project_manager'] },
  { label: 'nav.quotes', href: '/quotes', icon: FileText, roles: ['admin', 'project_manager', 'client'] },
  { label: 'nav.clients', href: '/clients', icon: Users2, roles: ['admin', 'project_manager'] },
  { label: 'nav.tickets', href: '/tickets', icon: Ticket },
  { label: 'nav.invoices', href: '/invoices', icon: Receipt, roles: ['admin', 'project_manager', 'support_staff', 'client'] },
  { label: 'nav.calendar', href: '/calendar', icon: CalendarDays },
  { label: 'nav.activity', href: '/activity', icon: Activity },
  { label: 'nav.reports', href: '/reports', icon: BarChart3, roles: ['admin', 'project_manager'] },
  { label: 'nav.siteMonitoring', href: '/site-monitoring', icon: HeartPulse, roles: ['admin', 'project_manager'] },
  { label: 'nav.settings', href: '/settings', icon: Settings, roles: ['admin'] },
];

/** Client-specific navigation — simplified for portal users */
const clientNavItems: NavItem[] = [
  { label: 'nav.dashboard', href: '/', icon: LayoutDashboard },
  { label: 'nav.projects', href: '/projects', icon: FolderKanban },
  { label: 'nav.quotes', href: '/quotes', icon: FileText },
  { label: 'nav.invoices', href: '/invoices', icon: Receipt },
  { label: 'nav.tickets', href: '/tickets', icon: Ticket },
  { label: 'nav.calendar', href: '/calendar', icon: CalendarDays },
];

/** Client mobile bottom nav keys */
const clientBottomNavKeys = ['/', '/projects', '/quotes', '/tickets'];

/** Key items shown in the mobile bottom nav bar — picked for universal access */
const bottomNavKeys = ['/', '/projects', '/tasks', '/clients'];

export function Sidebar() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useThemeStore();
  const { user, logout } = useAuthStore();
  const { data: settingsData } = usePortalSettings();
  const { data: navCountsData } = useNavCounts();
  const markSectionRead = useMarkSectionRead();
  const navCounts = navCountsData?.data || {};
  const settings = settingsData?.data || {};

  const isClient = user?.role === 'client';
  const portalName = settings.portalName || 'DynDev';
  const accentColor = settings.accentColor || '#1ce783';
  const logoUrl = settings.logoUrl || '';
  const brandFirst = portalName.split(' ')[0];
  const isDynDevBrand = brandFirst === 'DynDev';

  useEffect(() => {
    // Close mobile sidebar on route change
    setMobileOpen(false);
  }, [location.pathname]);

  const filteredItems = isClient
    ? clientNavItems
    : navItems.filter(
        (item) => !item.roles || item.roles.includes(user?.role || '')
      );

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  // Mobile bottom nav items — respect role filtering + client nav
  const activeNavItems = isClient
    ? clientNavItems.filter((item) => clientBottomNavKeys.includes(item.href))
    : navItems.filter(
        (item) =>
          bottomNavKeys.includes(item.href) &&
          (!item.roles || item.roles.includes(user?.role || ''))
      );

  const toggleCommandPalette = useCallback(() => {
    window.dispatchEvent(new CustomEvent('toggle-command-palette'));
  }, []);

  return (
    <>
      {/* Mobile top bar with hamburger, branding, Cmd+K, and notifications */}
      <div data-testid="mobile-top-bar" className="fixed top-0 left-0 right-0 z-[45] flex items-center justify-between px-4 py-2.5 md:hidden bg-[var(--bg-primary)]/90 backdrop-blur-md border-b border-[var(--border-color)]">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-1 rounded-lg hover:bg-[var(--bg-secondary)] text-[var(--text-primary)] transition-colors"
            aria-label={t('nav.toggleMenu')}
            data-testid="hamburger-menu"
          >
            <Menu size={20} />
          </button>
          <Link to="/" className="text-lg font-bold tracking-tight">{logoUrl ? (
                <img src={logoUrl} alt={portalName} className="h-7" />
              ) : (
                <span>{isDynDevBrand ? 'Dyn' : brandFirst}<span className="text-dyndev-green">{isDynDevBrand ? 'Dev' : ''}</span></span>
              )}
          </Link>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleCommandPalette}
            className="p-2 rounded-lg hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            aria-label={t('nav.searchPages', 'Search pages')}
            title="Cmd+K"
          >
            <Search size={18} />
          </button>
          <NotificationBell />
        </div>
      </div>

      {/* Spacer for fixed mobile top bar (prevents content overlap) */}
      <div className="h-[52px] md:hidden" />

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          data-testid="mobile-overlay"
          className="fixed inset-0 bg-black/60 z-[55] md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Desktop sidebar + Mobile drawer */}
      <aside
        className={cn(
          'sidebar-transition fixed md:sticky top-0 left-0 z-[55] h-screen bg-[var(--bg-secondary)] border-r border-[var(--border-color)] flex flex-col transition-all duration-300',
          collapsed ? 'w-[68px]' : 'w-[260px]',
          // Mobile: slide in from left
          'md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Logo + collapse button */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">            <Link
              to="/"
              className={cn(
                'text-xl font-bold tracking-tight hover:opacity-90 transition-opacity',
                collapsed && 'md:hidden'
              )}
            >
              {logoUrl ? (
                <img src={logoUrl} alt={portalName} className="h-8" />
              ) : (
                <span>{isDynDevBrand ? 'Dyn' : brandFirst}<span style={{ color: isClient ? accentColor : undefined }} className={cn(!isClient && 'text-dyndev-green')}>{isDynDevBrand ? 'Dev' : ''}</span></span>
              )}
            </Link>
          <div className="flex items-center gap-1">
            {/* Close button (mobile) */}
            <button
              onClick={() => setMobileOpen(false)}
              className="md:hidden p-1.5 rounded-lg hover:bg-[var(--bg-primary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
            {/* Collapse toggle (desktop) */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden md:flex p-1.5 rounded-lg hover:bg-[var(--bg-primary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredItems.map((item) => {
            const active = isActive(item.href);
            // Determine the nav section key for badge count
            const sectionKey = item.href === '/' ? 'dashboard' : item.href.replace(/^\//, '');
            const count = navCounts[sectionKey] || 0;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => {
                  setMobileOpen(false);
                  if (count > 0) markSectionRead.mutate(sectionKey, { onError: () => {} });
                }}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                  active
                    ? isClient
                      ? 'border'
                      : 'bg-dyndev-green/10 text-dyndev-green border border-dyndev-green/20'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-primary)]',
                  collapsed && 'md:justify-center md:px-2'
                )}
                style={active && isClient ? { backgroundColor: `${accentColor}15`, color: accentColor, borderColor: `${accentColor}30` } : undefined}
                title={collapsed ? t(item.label) : undefined}
              >
                <item.icon size={20} className="flex-shrink-0" />
                <span className={cn('flex-1', collapsed && 'md:hidden')}>{t(item.label)}</span>
                {count > 0 && (
                  <span className={cn(
                    'flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold',
                    'bg-red-500 text-white',
                    collapsed && 'md:hidden'
                  )}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom actions */}
        <div className="p-2 border-t border-[var(--border-color)] space-y-1">
          {/* Search / Cmd+K (desktop) */}
          <button
            onClick={toggleCommandPalette}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-primary)]',
              collapsed && 'md:justify-center md:px-2'
            )}
            title={collapsed ? t('nav.searchPages', 'Search pages') + ' (Cmd+K)' : undefined}
          >
            <Search size={20} className="flex-shrink-0" />
            <span className={cn('flex-1 text-left', collapsed && 'md:hidden')}>
              {t('nav.searchPages', 'Search pages')}
            </span>
            <kbd className={cn('hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-[var(--bg-secondary)]/50 text-[var(--text-secondary)] border border-[var(--border-color)]', collapsed && 'md:hidden')}>
              <span>⌘</span>K
            </kbd>
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className={cn(
              'group flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-primary)]',
              collapsed && 'md:justify-center md:px-2'
            )}
            title={collapsed ? t('nav.toggleTheme') : undefined}
          >
            <span className="relative flex-shrink-0">
              <Sun
                size={20}
                className={`transition-all duration-500 ${
                  theme === 'dark'
                    ? 'opacity-100 rotate-0 scale-100'
                    : 'opacity-0 rotate-90 scale-75 absolute inset-0'
                }`}
              />
              <Moon
                size={20}
                className={`transition-all duration-500 ${
                  theme === 'light'
                    ? 'opacity-100 rotate-0 scale-100'
                    : 'opacity-0 -rotate-90 scale-75 absolute inset-0'
                }`}
              />
            </span>
            <span className={cn(collapsed && 'md:hidden')}>
              {theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
            </span>
          </button>

          {/* Language switcher */}
          <LanguageSwitcher collapsed={collapsed} />

          {/* User info */}
          <Link
            to="/profile"
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors hover:bg-[var(--bg-primary)]',
              collapsed && 'md:justify-center'
            )}
          >
            <Avatar className="w-8 h-8">
              <AvatarFallback className="text-xs font-bold bg-dyndev-green/20 text-dyndev-green">
                {user?.name?.charAt(0)?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            <div className={cn('flex-1 min-w-0', collapsed && 'md:hidden')}>
              <p className="text-sm font-medium truncate">{user?.name || t('nav.userLabel')}</p>
              <p className="text-xs text-[var(--text-secondary)] capitalize truncate">
                {user?.role?.replace('_', ' ') || ''}
              </p>
            </div>
          </Link>

          {/* Logout */}
          <button
            onClick={() => {
              logout();
              toast({ title: t('nav.signedOut'), description: t('nav.signedOutDesc') });
            }}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-red-400 hover:text-red-300 hover:bg-red-500/10',
              collapsed && 'md:justify-center md:px-2'
            )}
            title={collapsed ? t('nav.logout') : undefined}
          >
            <LogOut size={20} />
            <span className={cn(collapsed && 'md:hidden')}>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile bottom navigation bar */}
      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 left-0 right-0 z-[50] md:hidden bg-[var(--bg-primary)]/95 backdrop-blur-md border-t border-[var(--border-color)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center justify-around px-2 py-1 max-w-lg mx-auto">
          {activeNavItems.map((item) => {
            const active = isActive(item.href);
            const mSectionKey = item.href === '/' ? 'dashboard' : item.href.replace(/^\//, '');
            const mCount = navCounts[mSectionKey] || 0;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => {
                  if (mCount > 0) markSectionRead.mutate(mSectionKey, { onError: () => {} });
                }}
                className={cn(
                  'flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all duration-200 min-w-0 relative',
                  active
                    ? isClient
                      ? 'text-[var(--text-primary)]'
                      : 'text-dyndev-green'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] active:scale-95',
                )}
                style={active && isClient ? { color: accentColor } : undefined}
              >
                {/* Active indicator dot */}
                {active && !isClient && (
                  <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-dyndev-green" />
                )}
                <item.icon size={22} strokeWidth={active ? 2.5 : 1.5} />
                <span className="text-[10px] font-medium truncate max-w-[60px]">
                  {t(item.label)}
                </span>
                {mCount > 0 ? (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold bg-red-500 text-white">
                      {mCount > 9 ? '9+' : mCount}
                    </span>
                  ) : null}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Spacer for mobile bottom nav (prevents content from being hidden behind the nav bar) */}
      <div className="h-[68px] md:hidden" />
    </>
  );
}
